import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGroupsStore } from 'src/store/groups';
import { useAuthStore } from 'src/store/auth';
import { RoleType } from 'src/constants';
import type { GroupResponse } from 'src/services/groups';

const GroupDetail = () => {
    const { groupId } = useParams<{ groupId: string }>();
    const navigate = useNavigate();
    const { user: currentUser } = useAuthStore();
    const { current, loading, error, loadOne, update, remove, uploadImage } = useGroupsStore();

    //   Check if user is admin, redirect to root if not
    useEffect(() => {
        if (currentUser && currentUser.role !== RoleType.Admin) {
            navigate('/');
        }
    }, [currentUser, navigate]);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [host, setHost] = useState('');
    const [hostError, setHostError] = useState('');
    const [showSettings, setShowSettings] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    // Settings state
    const [textsWelcomeText, setTextsWelcomeText] = useState('');
    const [textsWelcomeTitle, setTextsWelcomeTitle] = useState('');
    const [imagesLogo, setImagesLogo] = useState('');
    const [imagesWelcome, setImagesWelcome] = useState('');
    const [imagesEmailHeader, setImagesEmailHeader] = useState('');
    const [imagesEmailFooter, setImagesEmailFooter] = useState('');
    const [emailsPrimary, setEmailsPrimary] = useState('');
    const [emailsSecondary, setEmailsSecondary] = useState('');
    const [emailsTertiary, setEmailsTertiary] = useState('');
    const [phonesPrimary, setPhonesPrimary] = useState('');
    const [phonesSecondary, setPhonesSecondary] = useState('');
    const [phonesTertiary, setPhonesTertiary] = useState('');
    const [colorsPrimary, setColorsPrimary] = useState('');
    const [colorsSecondary, setColorsSecondary] = useState('');
    const [colorsTertiary, setColorsTertiary] = useState('');
    const [colorsQuaternary, setColorsQuaternary] = useState('');
    const [colorsQuinary, setColorsQuinary] = useState('');
    const [colorsSenary, setColorsSenary] = useState('');
    const [colorsSeptenary, setColorsSeptenary] = useState('');
    const [colorsOctonary, setColorsOctonary] = useState('');
    const [colorsNonary, setColorsNonary] = useState('');
    const [colorsDenary, setColorsDenary] = useState('');
    // Current images loaded for editing group
    const [currentGroupImages, setCurrentGroupImages] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        if (groupId) {
            loadOne(groupId);
        }
    }, [groupId, loadOne]);

    // Update form fields when current group data loads
    useEffect(() => {
        if (current) {
            setName(current.name || '');
            setDescription(current.description || '');
            setHost(current.host || '');

            const settings = current.settings;
            setTextsWelcomeText((settings?.texts as any)?.welcomeText || '');
            setTextsWelcomeTitle((settings?.texts as any)?.welcomeTitle || '');
            setImagesLogo(settings?.images?.logo || '');
            setImagesWelcome(settings?.images?.welcome || '');
            setImagesEmailHeader(settings?.images?.emailHeader || '');
            setImagesEmailFooter(settings?.images?.emailFooter || '');
            setEmailsPrimary(settings?.emails?.primary || '');
            setEmailsSecondary(settings?.emails?.secondary || '');
            setEmailsTertiary(settings?.emails?.tertiary || '');
            setPhonesPrimary(settings?.phones?.primary || '');
            setPhonesSecondary(settings?.phones?.secondary || '');
            setPhonesTertiary(settings?.phones?.tertiary || '');

            const colors = settings?.colors;
            if (typeof colors === 'object' && colors !== null) {
                setColorsPrimary(colors?.primary || '');
                setColorsSecondary(colors?.secondary || '');
                setColorsTertiary(colors?.tertiary || '');
                setColorsQuaternary(colors?.quaternary || '');
                setColorsQuinary(colors?.quinary || '');
                setColorsSenary(colors?.senary || '');
                setColorsSeptenary(colors?.septenary || '');
                setColorsOctonary(colors?.octonary || '');
                setColorsNonary(colors?.nonary || '');
                setColorsDenary(colors?.denary || '');
            }

            const images: { [key: string]: string } = {};
            if (settings?.images?.logo) images[`${current.id}_logo`] = settings.images.logo;
            if (settings?.images?.welcome) images[`${current.id}_welcome`] = settings.images.welcome;
            if (settings?.images?.emailHeader) images[`${current.id}_emailHeader`] = settings.images.emailHeader;
            if (settings?.images?.emailFooter) images[`${current.id}_emailFooter`] = settings.images.emailFooter;
            setCurrentGroupImages(images);
        }
    }, [current]);

    const buildSettingsPayload = () => {
        const settings: any = {};
        const texts: any = {};
        const images: any = {};
        const emails: any = {};
        const phones: any = {};
        const colors: any = {};

        if (textsWelcomeText) texts.welcomeText = textsWelcomeText;
        if (textsWelcomeTitle) texts.welcomeTitle = textsWelcomeTitle;
        if (imagesLogo) images.logo = imagesLogo;
        if (imagesWelcome) images.welcome = imagesWelcome;
        if (imagesEmailHeader) images.emailHeader = imagesEmailHeader;
        if (imagesEmailFooter) images.emailFooter = imagesEmailFooter;
        if (emailsPrimary) emails.primary = emailsPrimary;
        if (emailsSecondary) emails.secondary = emailsSecondary;
        if (emailsTertiary) emails.tertiary = emailsTertiary;
        if (phonesPrimary) phones.primary = phonesPrimary;
        if (phonesSecondary) phones.secondary = phonesSecondary;
        if (phonesTertiary) phones.tertiary = phonesTertiary;
        if (colorsPrimary) colors.primary = colorsPrimary;
        if (colorsSecondary) colors.secondary = colorsSecondary;
        if (colorsTertiary) colors.tertiary = colorsTertiary;
        if (colorsQuaternary) colors.quaternary = colorsQuaternary;
        if (colorsQuinary) colors.quinary = colorsQuinary;
        if (colorsSenary) colors.senary = colorsSenary;
        if (colorsSeptenary) colors.septenary = colorsSeptenary;
        if (colorsOctonary) colors.octonary = colorsOctonary;
        if (colorsNonary) colors.nonary = colorsNonary;
        if (colorsDenary) colors.denary = colorsDenary;

        if (Object.keys(texts).length) settings.texts = texts;
        if (Object.keys(images).length) settings.images = images;
        if (Object.keys(emails).length) settings.emails = emails;
        if (Object.keys(phones).length) settings.phones = phones;
        if (Object.keys(colors).length) settings.colors = colors;

        return Object.keys(settings).length ? settings : undefined;
    };

    // Host validation function - reactive validation
    const validateHost = (hostValue: string): boolean => {
        if (!hostValue.trim()) {
            setHostError('Host is required');
            return false;
        }

        const trimmedHost = hostValue.trim();

        // Check for protocol indicators - this is the main validation
        if (trimmedHost.includes('://') || trimmedHost.startsWith('http://') || trimmedHost.startsWith('https://')) {
            setHostError('Host must be in format: w/o http(s) protocol and valid url');
            return false;
        }

        // Basic URL validation regex for clean host
        const urlRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

        if (!urlRegex.test(trimmedHost)) {
            setHostError('Host must be in format: w/o http(s) protocol and valid url');
            return false;
        }

        setHostError('');
        return true;
    };

    const handleFileUpload = async (file: File, imageType: 'logo' | 'welcome' | 'emailHeader' | 'emailFooter', groupId: string | number) => {
        try {
            const result: GroupResponse = await uploadImage(groupId, file, imageType);

            const reader = new FileReader();
            reader.onload = (e) => {
                const previewUrl = e.target?.result as string;
                if (previewUrl) {
                    setCurrentGroupImages(prev => ({
                        ...prev,
                        [`${groupId}_${imageType}`]: previewUrl
                    }));
                }
            };
            reader.readAsDataURL(file);

            if (current && current.id === groupId) {
                const uploadedImageUrl = result?.settings?.images?.[imageType];

                if (uploadedImageUrl) {
                    switch (imageType) {
                        case 'logo':
                            setImagesLogo(uploadedImageUrl);
                            break;
                        case 'welcome':
                            setImagesWelcome(uploadedImageUrl);
                            break;
                        case 'emailHeader':
                            setImagesEmailHeader(uploadedImageUrl);
                            break;
                        case 'emailFooter':
                            setImagesEmailFooter(uploadedImageUrl);
                            break;
                    }
                } else {
                    // Reload the group data
                    await loadOne(groupId);
                }
            }
        } catch (error) {
            console.error('Failed to upload image:', error);
        }
    };

    const handleUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!groupId) return;
        // Validate host before proceeding
        if (!validateHost(host)) {
            return;
        }
        const settings = buildSettingsPayload();
        const status = typeof current?.status === 'number' ? current.status : 0;
        await update(groupId, { name, description, host, status, settings });
        navigate('/admin-groups');
    };

    const handleDelete = async () => {
        if (!groupId) return;
        await remove(groupId);
        navigate('/admin-groups');
    };

    const handleBackToList = () => {
        navigate('/admin-groups');
    };

    if (loading) {
        return (
            <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative flex items-center justify-center">
                <div className="text-lg">Loading...</div>
            </div>
        );
    }

    if (error || !current) {
        return (
            <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative flex items-center justify-center">
                <div className="text-lg text-red-600">{error || 'Group not found'}</div>
            </div>
        );
    }

    return (
        <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative">
            {/* Header */}
            <div className="px-8 py-6 flex items-center justify-between max-md:p-2">
                <div className="flex items-center gap-4">
                    <button
                        onClick={handleBackToList}
                        className="p-2 hover:bg-gray-100 flex items-center rounded-full"
                    >
                        <img src="/icons/left.svg" alt="back" />
                    </button>
                    <h1 className="id-card-title font-medium max-lg:!text-2xl max-md:!text-xl">
                        {current.name}
                    </h1>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setConfirmOpen(true)}
                        className="id-card-button-primary px-4 py-2 rounded-full"
                    >
                        Delete Group
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="px-8 py-6 max-md:px-2">
                <div className="bg-white-overlay big-corner p-6 max-md:p-4">
                    <form onSubmit={handleUpdate} className="grid gap-2 max-w-3xl">
                        <input
                            className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]"
                            placeholder="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                        <input
                            className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]"
                            placeholder="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            required
                        />
                        <div>
                            <input
                                className={`px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full ${hostError ? 'border-red-500' : ''}`}
                                placeholder="Host"
                                value={host}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    setHost(newValue);
                                    if (newValue.trim()) {
                                        if (newValue.includes('://') || newValue.startsWith('http://') || newValue.startsWith('https://')) {
                                            setHostError('Host must be in format: w/o http(s) protocol and valid url');
                                        } else {
                                            const urlRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;
                                            if (!urlRegex.test(newValue.trim())) {
                                                setHostError('Host must be in format: w/o http(s) protocol and valid url');
                                            } else {
                                                setHostError('');
                                            }
                                        }
                                    } else {
                                        setHostError('');
                                    }
                                }}
                                required
                            />
                            {hostError && (
                                <p className="text-sm text-red-600 mt-1">{hostError}</p>
                            )}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                            <input id="toggle-settings" type="checkbox" checked={showSettings} onChange={(e) => setShowSettings(e.target.checked)} />
                            <label htmlFor="toggle-settings" className="text-sm">Show settings</label>
                        </div>
                        {showSettings && (
                            <div className="grid gap-2 mt-2">
                                <h3 className="text-lg">Settings</h3>
                                <div className="grid gap-2">
                                    <div className="grid gap-1">
                                        <span className="text-sm">Texts</span>
                                        <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Welcome title" value={textsWelcomeTitle} onChange={(e) => setTextsWelcomeTitle(e.target.value)} />
                                        <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Welcome text" value={textsWelcomeText} onChange={(e) => setTextsWelcomeText(e.target.value)} />
                                    </div>
                                    <div className="grid gap-1">
                                        <span className="text-sm">Images</span>
                                        <div className="mt-3 p-3 bg-[var(--gray-50)] rounded border">
                                            <h4 className="text-sm font-medium text-[var(--gray-700)] mb-2">Upload Files</h4>
                                            <div className="grid grid-cols-2 gap-3">
                                                {(['logo', 'welcome', 'emailHeader', 'emailFooter'] as const).map((imageType) => {
                                                    const imageKey = `${current.id}_${imageType}`;
                                                    const currentImage = currentGroupImages[imageKey];

                                                    return (
                                                        <div key={imageType} className="flex flex-col gap-1">
                                                            <label className="text-xs text-[var(--gray-600)] font-medium">{imageType}</label>
                                                            {currentImage ? (
                                                                <div className="relative">
                                                                    <img
                                                                        src={currentImage}
                                                                        alt={`${imageType} preview`}
                                                                        className="w-full h-20 object-cover rounded border mb-1"
                                                                    />
                                                                    <input
                                                                        type="file"
                                                                        accept="image/*"
                                                                        className="px-2 py-1 rounded text-xs border border-[var(--gray-300)]"
                                                                        onChange={(e) => {
                                                                            const file = e.target.files?.[0];
                                                                            if (file) {
                                                                                handleFileUpload(file, imageType, String(current.id));
                                                                            }
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="absolute top-1 right-1 bg-red-500 text-white text-xs px-1 rounded"
                                                                        onClick={() => {
                                                                            setCurrentGroupImages(prev => {
                                                                                const newImages = { ...prev };
                                                                                delete newImages[imageKey];
                                                                                return newImages;
                                                                            });
                                                                        }}
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    className="px-2 py-1 rounded text-xs border border-[var(--gray-300)]"
                                                                    onChange={(e) => {
                                                                        const file = e.target.files?.[0];
                                                                        if (file) {
                                                                            handleFileUpload(file, imageType, String(current.id));
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid gap-1">
                                        <span className="text-sm">Emails</span>
                                        <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Primary email" value={emailsPrimary} onChange={(e) => setEmailsPrimary(e.target.value)} />
                                        <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Secondary email" value={emailsSecondary} onChange={(e) => setEmailsSecondary(e.target.value)} />
                                        <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Tertiary email" value={emailsTertiary} onChange={(e) => setEmailsTertiary(e.target.value)} />
                                    </div>
                                    <div className="grid gap-1">
                                        <span className="text-sm">Phones</span>
                                        <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Primary phone" value={phonesPrimary} onChange={(e) => setPhonesPrimary(e.target.value)} />
                                        <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Secondary phone" value={phonesSecondary} onChange={(e) => setPhonesSecondary(e.target.value)} />
                                        <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Tertiary phone" value={phonesTertiary} onChange={(e) => setPhonesTertiary(e.target.value)} />
                                    </div>
                                    <div className="grid gap-1">
                                        <span className="text-sm">Colors</span>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="flex items-center gap-2"><span className="w-24 text-sm">Primary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsPrimary || '#000000'} onChange={(e) => setColorsPrimary(e.target.value)} /></label>
                                                <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsPrimary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsPrimary(v); }} />
                                            </div>
                                            <div>
                                                <label className="flex items-center gap-2"><span className="w-24 text-sm">Secondary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsSecondary || '#000000'} onChange={(e) => setColorsSecondary(e.target.value)} /></label>
                                                <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsSecondary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsSecondary(v); }} />
                                            </div>
                                            <div>
                                                <label className="flex items-center gap-2"><span className="w-24 text-sm">Tertiary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsTertiary || '#000000'} onChange={(e) => setColorsTertiary(e.target.value)} /></label>
                                                <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsTertiary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsTertiary(v); }} />
                                            </div>
                                            <div>
                                                <label className="flex items-center gap-2"><span className="w-24 text-sm">Quaternary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsQuaternary || '#000000'} onChange={(e) => setColorsQuaternary(e.target.value)} /></label>
                                                <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsQuaternary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsQuaternary(v); }} />
                                            </div>
                                            <div>
                                                <label className="flex items-center gap-2"><span className="w-24 text-sm">Quinary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsQuinary || '#000000'} onChange={(e) => setColorsQuinary(e.target.value)} /></label>
                                                <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsQuinary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsQuinary(v); }} />
                                            </div>
                                            <div>
                                                <label className="flex items-center gap-2"><span className="w-24 text-sm">Senary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsSenary || '#000000'} onChange={(e) => setColorsSenary(e.target.value)} /></label>
                                                <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsSenary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsSenary(v); }} />
                                            </div>
                                            <div>
                                                <label className="flex items-center gap-2"><span className="w-24 text-sm">Septenary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsSeptenary || '#000000'} onChange={(e) => setColorsSeptenary(e.target.value)} /></label>
                                                <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsSeptenary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsSeptenary(v); }} />
                                            </div>
                                            <div>
                                                <label className="flex items-center gap-2"><span className="w-24 text-sm">Octonary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsOctonary || '#000000'} onChange={(e) => setColorsOctonary(e.target.value)} /></label>
                                                <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsOctonary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsOctonary(v); }} />
                                            </div>
                                            <div>
                                                <label className="flex items-center gap-2"><span className="w-24 text-sm">Nonary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsNonary || '#000000'} onChange={(e) => setColorsNonary(e.target.value)} /></label>
                                                <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsNonary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsNonary(v); }} />
                                            </div>
                                            <div>
                                                <label className="flex items-center gap-2"><span className="w-24 text-sm">Denary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsDenary || '#000000'} onChange={(e) => setColorsDenary(e.target.value)} /></label>
                                                <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsDenary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsDenary(v); }} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <button
                                type="submit"
                                className={`id-card-button-primary px-4 py-2 rounded-full w-fit ${(loading || !name.trim() || !description.trim() || !host.trim() || hostError) ? 'opacity-60 cursor-not-allowed' : ''}`}
                                disabled={loading || !name.trim() || !description.trim() || !host.trim() || !!hostError}
                            >
                                Update Group
                            </button>
                            <button
                                type="button"
                                className="id-card-button-secondary px-4 py-2 rounded-full"
                                onClick={handleBackToList}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Delete Confirmation */}
            {confirmOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setConfirmOpen(false)} />
                    <div className="relative bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">Delete Group</h3>
                        <p className="text-gray-600 mb-6">Are you sure you want to delete this group? This action cannot be undone.</p>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => setConfirmOpen(false)}
                                className="id-card-button-secondary px-4 py-2 rounded-full"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className={"id-card-button-primary w-full px-4 py-2 rounded-full"}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GroupDetail;
